import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Field, Input } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTenant } from '../contexts/TenantContext';
import { administrativeLoginUrl } from '../lib/tenant';

export default function LoginPage() {
  const { user, login } = useAuth();
  const { show } = useToast();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const rootLoginUrl = tenant ? administrativeLoginUrl(tenant.subdomain) : null;

  if (user) {
    const target = user.role === 'super_admin' ? '/admin' : user.role === 'reception' ? '/dashboard/reception' : user.role === 'kitchen' ? '/dashboard/kitchen' : '/dashboard';
    return <Navigate to={target} replace />;
  }

  if (tenant && rootLoginUrl) {
    return (
      <main className="app-brand-shell grid min-h-screen place-items-center p-4">
        <Card className="w-full max-w-md p-7 text-center sm:p-9">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[var(--app-brand-ink)] text-xl font-black text-white">EP</div>
          <h1 className="mt-5 text-2xl font-black text-[var(--app-brand-ink)]">Acceso de {tenant.name}</h1>
          <p className="mt-2 text-sm leading-6 text-[var(--app-brand-muted)]">Los empleados entran desde este subdominio. Propietarios, gerentes y superadministradores usan el dominio principal para mantener las cargas R2 y la sesión administrativa en un único origen.</p>
          <div className="mt-6 grid gap-3">
            <Link to="/employee"><Button className="w-full">Entrar como empleado</Button></Link>
            <a href={rootLoginUrl}><Button variant="secondary" className="w-full">Abrir panel administrativo</Button></a>
            <Link to="/" className="text-sm font-semibold text-[var(--app-brand-muted)]">Volver al menú</Link>
          </div>
        </Card>
      </main>
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const next = await login(email, password);
      const fallback = next.role === 'super_admin' ? '/admin' : '/dashboard';
      const requested = (location.state as { from?: string } | null)?.from;
      navigate(requested || fallback, { replace: true });
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo iniciar sesión.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="app-brand-shell grid min-h-screen p-4 lg:grid-cols-2 lg:p-0">
      <section className="hidden place-items-center bg-[var(--app-brand-ink)] p-12 text-white lg:grid">
        <div className="max-w-lg">
          <div className="app-brand-kicker">Estoy Pidiendo</div>
          <h1 className="mt-5 text-5xl font-black leading-tight">Administra menús, sucursales y pedidos en tiempo real.</h1>
          <p className="mt-5 leading-7 text-white/70">Una sola plataforma para propietarios, gerentes, recepción y cocina.</p>
        </div>
      </section>
      <section className="grid place-items-center rounded-3xl bg-[var(--app-brand-paper)] p-5 lg:rounded-none">
        <Card className="w-full max-w-md p-7 sm:p-9">
          <h2 className="text-2xl font-black text-[var(--app-brand-ink)]">Iniciar sesión</h2>
          <p className="mt-1 text-sm text-[var(--app-brand-muted)]">Acceso para superadmin, propietarios y gerentes.</p>
          <form onSubmit={submit} className="mt-6 grid gap-4">
            <Field label="Correo"><Input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field>
            <Field label="Contraseña"><Input type="password" required minLength={8} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></Field>
            <Button type="submit" busy={busy} className="mt-2 w-full">Entrar</Button>
          </form>
          <div className="mt-5 border-t border-[var(--app-brand-line)] pt-5 text-center text-sm text-[var(--app-brand-muted)]">
            {tenant ? (
              <>¿Trabajas en recepción o cocina? <Link to="/employee" className="font-bold text-[var(--app-brand-ink)]">Entrar con PIN</Link></>
            ) : (
              <>Los empleados entran con PIN desde el subdominio público de su negocio.</>
            )}
          </div>
          {tenant && <Link to="/" className="mt-3 block text-center text-xs font-semibold text-[var(--app-brand-muted)] hover:text-[var(--app-brand-ink)]">Volver al menú público</Link>}
          {import.meta.env.DEV && (
            <div className="mt-5 rounded-xl bg-amber-50 p-3 text-xs leading-5 text-amber-800">
              Demo local después de ejecutar el seed: admin@menucloud.local / Admin123! y owner@demo.local / Owner123!
            </div>
          )}
        </Card>
      </section>
    </main>
  );
}
